/**
 * Constants, types, and pure helper functions for the FieldMapping editor.
 *
 * Everything here is stateless and framework-agnostic — no React imports.
 */
import type { DatasetViewColumn } from "../../../api/types";
import type {
  ChartTypeOption,
  AggregationType,
  SortOption,
} from "../visualConfig";
import { BREAKDOWN_RULES } from "../visualConfig";
import { CHART_AGGREGATIONS } from "../../../generated/chartAggregations";

/* ------------------------------------------------------------------ */
/*  Sort & Limit dropdown options                                      */
/* ------------------------------------------------------------------ */

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "none", label: "None" },
  { value: "value_desc", label: "Value ↓" },
  { value: "value_asc", label: "Value ↑" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
];

export const LIMIT_OPTIONS: { value: string; label: string }[] = [
  { value: "none", label: "None" },
  { value: "5", label: "Top 5" },
  { value: "10", label: "Top 10" },
  { value: "20", label: "Top 20" },
  { value: "50", label: "Top 50" },
  { value: "custom", label: "Custom…" },
];

export const AGGREGATIONS: readonly { value: AggregationType; label: string }[] =
  CHART_AGGREGATIONS;

/** Aggregation options for table columns — includes "None" (= no aggregation). */
export const TABLE_AGGREGATIONS: readonly { value: AggregationType; label: string }[] = [
  { value: "none", label: "None" },
  ...CHART_AGGREGATIONS,
];

/* ------------------------------------------------------------------ */
/*  Semantic badges & group labels                                     */
/* ------------------------------------------------------------------ */

export const SEMANTIC_BADGES: Record<string, string> = {
  dimension: "DIM",
  measure: "NUM",
  time: "TIME",
  id: "ID",
};

const SEMANTIC_GROUP_LABELS: Record<string, string> = {
  time: "Time",
  dimension: "Dimensions",
  measure: "Measures",
  id: "IDs",
};

/* ------------------------------------------------------------------ */
/*  Dropdown positioning config                                        */
/* ------------------------------------------------------------------ */

/**
 * Positioning config for column-picker dropdowns.
 * Tries "below" first, falls back to "above" (never side-placement).
 * autoSize constrains the popup height to available viewport space.
 */
export const COLUMN_PICKER_POSITIONING = {
  position: "below" as const,
  fallbackPositions: ["above" as const],
  autoSize: "height-always" as const,
};

/* ------------------------------------------------------------------ */
/*  Column grouping helpers                                            */
/* ------------------------------------------------------------------ */

export type SlotType = "x" | "y" | "breakdown" | "measure_x";

/** Semantic group ordering per slot type */
const SLOT_GROUP_ORDER: Record<SlotType, string[]> = {
  x: ["time", "dimension", "measure", "id"],
  measure_x: ["measure", "dimension", "time", "id"],
  y: ["measure", "dimension", "id", "time"],
  breakdown: ["dimension", "time"],
};

export interface ColumnGroup {
  label: string;
  semanticType: string;
  columns: DatasetViewColumn[];
}

/**
 * Group and order columns by semantic type for a given slot.
 * Only includes groups that have at least one column.
 */
export function groupColumnsForSlot(
  columns: DatasetViewColumn[],
  slot: SlotType,
): ColumnGroup[] {
  const order = SLOT_GROUP_ORDER[slot];
  const groups: ColumnGroup[] = [];

  for (const st of order) {
    const cols = columns.filter((c) => c.semantic_type === st);
    if (cols.length > 0) {
      groups.push({
        label: SEMANTIC_GROUP_LABELS[st] ?? st,
        semanticType: st,
        columns: cols,
      });
    }
  }

  // Any remaining columns not in the order (safety net)
  const covered = new Set(order);
  const remaining = columns.filter((c) => !covered.has(c.semantic_type));
  if (remaining.length > 0) {
    groups.push({ label: "Other", semanticType: "other", columns: remaining });
  }

  return groups;
}

/* ------------------------------------------------------------------ */
/*  Dynamic role labels per chart type                                 */
/* ------------------------------------------------------------------ */

export interface FieldRoles {
  xLabel: string;
  /** Slot ordering for the X column picker */
  xSlot: SlotType;
  yLabel: string;
  /** If true, Y is an automatic count — no column/agg picker shown (histogram) */
  yAutoCount: boolean;
  /** Label for a second Y-axis dimension picker (heatmap). null = hidden. */
  yDimensionLabel: string | null;
  seriesLabel: string | null;
  seriesHelper: string | null;
  /** If true, user can add multiple Y measures (bar, line, area) */
  multiMeasure: boolean;
  /** Show optional Size field (scatter/bubble) */
  showSize: boolean;
  /** Hide aggregation dropdowns (scatter raw mode) */
  hideAggregation: boolean;
  /** Show raw/aggregated mode toggle (scatter) */
  showModeToggle: boolean;
}

export function getFieldRoles(chartType: ChartTypeOption): FieldRoles {
  switch (chartType) {
    case "kpi":
      return {
        xLabel: "Category",
        xSlot: "x",
        yLabel: "Value",
        yAutoCount: false,
        yDimensionLabel: null,
        seriesLabel: null,
        seriesHelper: null,
        multiMeasure: false,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    case "table":
      return {
        xLabel: "Group by",
        xSlot: "x",
        yLabel: "Columns",
        yAutoCount: false,
        yDimensionLabel: null,
        seriesLabel: null,
        seriesHelper: null,
        multiMeasure: true,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    case "pie":
      return {
        xLabel: "Category",
        xSlot: "x",
        yLabel: "Value",
        yAutoCount: false,
        yDimensionLabel: null,
        seriesLabel: null,
        seriesHelper: null,
        multiMeasure: false,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    case "funnel":
      return {
        xLabel: "Stage",
        xSlot: "x",
        yLabel: "Value",
        yAutoCount: false,
        yDimensionLabel: null,
        seriesLabel: null,
        seriesHelper: null,
        multiMeasure: false,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    case "scatter":
      return {
        xLabel: "X axis",
        xSlot: "measure_x",
        yLabel: "Y axis",
        yAutoCount: false,
        yDimensionLabel: null,
        seriesLabel: BREAKDOWN_RULES.scatter.enabled ? "Color" : null,
        seriesHelper: BREAKDOWN_RULES.scatter.enabled
          ? "Color-code points by category"
          : null,
        multiMeasure: false,
        showSize: true,
        hideAggregation: false, // controlled dynamically by scatterMode
        showModeToggle: true,
      };
    case "heatmap":
      return {
        xLabel: "X axis",
        xSlot: "x",
        yLabel: "Value",
        yAutoCount: false,
        yDimensionLabel: "Y axis",
        seriesLabel: null,
        seriesHelper: null,
        multiMeasure: false,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    case "histogram":
      return {
        xLabel: "Bins",
        xSlot: "measure_x",
        yLabel: "Count",
        yAutoCount: true,
        yDimensionLabel: null,
        seriesLabel: BREAKDOWN_RULES.histogram.enabled ? "Breakdown" : null,
        seriesHelper: BREAKDOWN_RULES.histogram.enabled
          ? "Creates one series per value"
          : null,
        multiMeasure: false,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    default: {
      const rule = BREAKDOWN_RULES[chartType];
      return {
        xLabel: "X axis",
        xSlot: "x",
        yLabel: "Y values",
        yAutoCount: false,
        yDimensionLabel: null,
        seriesLabel: rule.enabled ? "Breakdown" : null,
        seriesHelper: rule.enabled ? "Creates one series per value" : null,
        multiMeasure: true,
        showSize: false,
        hideAggregation: false,
        showModeToggle: false,
      };
    }
  }
}

/* ------------------------------------------------------------------ */
/*  Misc helpers                                                       */
/* ------------------------------------------------------------------ */

/** Display label for a column (prefer display_name over raw name). */
export function colLabel(col: DatasetViewColumn): string {
  return col.display_name || col.name;
}

/** Resolve a column display label by name, falling back to the raw name. */
export function colLabelByName(
  name: string,
  columns: DatasetViewColumn[],
): string {
  const found = columns.find((c) => c.name === name);
  return found ? colLabel(found) : name;
}

/** Stable sortable id for a measure at a given index. */
export function measureId(index: number): string {
  return `measure-${index}`;
}